import mime from 'mime';
import { Buffer } from 'buffer';
import { PickedFile, PickedImage } from '../components/v2BaseComponents/ImagePicker';
import { Platform } from 'react-native';
import { convertHEICToPNG } from '../components/v2BaseComponents/ImagePicker/utils';

export type ProcessSystemFileReturnValue = {
    name: string;
    contentType: string;
    uri: string;
    s3UploadBuffer: Buffer;
    originalName?: string;
};

export type ProcessSystemFileArgs = {
    pickedImage: PickedImage | PickedFile;
};

const getUploadName = (pickedImage: PickedImage | PickedFile, contentType: string) => {
    const baseName =
        (pickedImage as PickedFile)?.originalName ||
        (pickedImage.uri.split('/').pop() || '').substring(0, 120) || // this can be base 64.  At that point it's essentially a random name but can be extremely long and cause errors
        new Date().toISOString();
    const extension = mime.getExtension(contentType);

    if (!extension || baseName.toLowerCase().endsWith(extension.toLowerCase())) return baseName;

    return `${baseName}.${extension}`;
};

const getContentTypeAndEncodingRegex = () => /^data:(?<contentType>.*);base64,(?<encoding>.*)$/;

export const base64EncodingFromUri = (uri: string) => {
    const { encoding } = uri.match(getContentTypeAndEncodingRegex())?.groups || {};
    if (!encoding) throw new Error('No encoding found');

    return encoding;
};

export default async function processSystemFile({
    pickedImage,
}: ProcessSystemFileArgs): Promise<ProcessSystemFileReturnValue> {
    if (!pickedImage?.uri) throw new Error('No file path provided');

    if (Platform.OS === 'web') {
        const { contentType } = pickedImage.uri.match(getContentTypeAndEncodingRegex())?.groups || {};
        if (!contentType) throw new Error('No content type found');

        if (contentType === 'image/heic' || contentType === 'image/heif') {
            const result = await convertHEICToPNG(
                pickedImage.uri,
                contentType,
                (pickedImage as PickedFile)?.originalName,
            );

            return result;
        } else {
            return {
                name: getUploadName(pickedImage, contentType),
                contentType,
                uri: pickedImage.uri,
                s3UploadBuffer: Buffer.from(pickedImage.base64, 'base64'),
                originalName: (pickedImage as PickedFile)?.originalName || undefined,
            };
        }
    }

    const contentType = mime.getType(pickedImage.uri);
    if (!contentType) throw new Error('No content type found');

    const name = pickedImage.uri.split('/').pop();

    const base64DataString = `data:${contentType};base64,${pickedImage.base64}`;

    return {
        name: name || '',
        contentType,
        uri: base64DataString,
        s3UploadBuffer: Buffer.from(pickedImage.base64, 'base64'),
        originalName: (pickedImage as PickedFile)?.originalName || undefined,
    };
}
